import React, { useState, useEffect } from 'react';
import { CreditCard, Plus, Trash2, RefreshCw, X } from 'lucide-react';
import { AddPaymentMethodModal } from './AddPaymentMethodModal';
import { useAuthStore } from '../../../stores/authStore'; // Adjust to your app
import { getPaymentMethods, deletePaymentMethod, setDefaultPaymentMethod } from '../../../utils/stripe/api';

interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

export function PaymentMethodList() {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deletePaymentMethodId, setDeletePaymentMethodId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const user = useAuthStore(state => state.user);

  const loadPaymentMethods = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const methods = await getPaymentMethods(user.id);
      setPaymentMethods(methods);
    } catch (err) {
      console.error('Failed to load payment methods:', err);
      setError('支払い方法の読み込みに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPaymentMethods();
  }, [user?.id]);


  const handleDelete = async () => {
   
    if (!deletePaymentMethodId) return;
    setIsDeleting(true);
    setError(null);

    try {
      await deletePaymentMethod(deletePaymentMethodId);
      setPaymentMethods(methods => methods.filter(method => method.id !== deletePaymentMethodId));
    } catch (err) {
      console.error('Failed to delete payment method:', err);
      setError('支払い方法の削除に失敗しました');
    } finally {
      setDeletePaymentMethodId(null);
    }

     setIsDeleting(false);
     setShowDeleteModal(false);
  }


  const handleSetDefault = async (id: string) => {
    if (!user?.id) return;
    setError(null);
    try {
      await setDefaultPaymentMethod(user.id, id);
      await loadPaymentMethods(); // Reload to ensure latest data/server-set default status
    } catch (err) {
      console.error('Failed to set default payment method:', err);
      setError('デフォルト支払い方法の設定に失敗しました');
    }
  };

  const handleAddPaymentMethod = (newMethod: PaymentMethod) => {
    setPaymentMethods([...paymentMethods, newMethod]);
    setShowAddModal(false);
    setTimeout(() => {
      loadPaymentMethods(); // Reload to ensure latest data/server-set default status
    }, 500);
  };

  return (
    <div className="">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">お支払い方法</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={loadPaymentMethods}
            disabled={isLoading}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            title="更新"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-50 hover:bg-blue-100"
          >
            <Plus className="w-4 h-4 mr-1" />
            追加
          </button>
        </div>
      </div>
      {error && (
        <div className="mt-4 p-3 bg-red-50 text-red-600 text-sm rounded-md">
          {error}
        </div>
      )}
      {isLoading ? (
        <div className="text-center py-6 bg-gray-50 rounded-lg mt-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto mb-3"></div>
          <p className="text-gray-500">読み込み中...</p>
        </div>
      ) : paymentMethods.length === 0 ? (
        <div className="text-center py-6 bg-gray-50 rounded-lg">
          <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">登録されているお支払い方法はありません</p>
        </div>
      ) : (
        <div className="space-y-3">
          {paymentMethods.map(method => (
            <div 
              key={method.id} 
              className={`flex items-center justify-between p-4 rounded-lg border ${
                method.isDefault ? 'border-blue-200 bg-blue-50' : 'border-gray-200'
              }`}
            >
              <div className="flex items-center">
                <div className="mr-3">
                  <CreditCard className={`w-8 h-8 ${method.isDefault ? 'text-blue-500' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {method.brand.charAt(0).toUpperCase() + method.brand.slice(1)} •••• {method.last4}
                  </p>
                  <p className="text-sm text-gray-500">
                    有効期限: {method.expMonth}/{method.expYear}
                    {method.isDefault && <span className="ml-2 text-blue-600 font-medium">デフォルト</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!method.isDefault && (
                  <button
                    onClick={() => handleSetDefault(method.id)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    デフォルトにする
                  </button>
                )}
                <button
                  onClick={() =>  
                        {
                          setDeletePaymentMethodId(method.id);
                          setShowDeleteModal(true);
                        }
                      }
                  disabled={deletePaymentMethodId === method.id}
                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {showAddModal && user && (
        <AddPaymentMethodModal
          userId={user.id}
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddPaymentMethod}
        />
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-md w-full mx-4">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">支払い方法を削除</h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                本当にこの支払い方法を削除しますか？この操作は取り消せません。
              </p>
              <div className="  flex justify-end gap-2">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                >
                  {isDeleting ? '削除中...' : '削除する'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}